/**
 * layoutMetadata.ts - Chart Layout Metadata
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
export const CHART_LAYOUTS = {
  hierarchical: {
    id: 'hierarchical',
    technicalName: 'hierarchical',
    displayName: 'Organization Chart',
    shortName: 'Org Chart',
    description: 'Top-down tree showing clear parent-child relationships',
    bestFor: [
      'Organization charts',
      'File systems',
      'Reporting structures',
      'Taxonomies',
      'Class hierarchies'
    ],
    icon: '🏢',
    thumbnail: '/assets/layouts/hierarchical.svg',
    decisionPath: {
      hasHierarchy: true,
      isProcessFlow: false
    },
    defaultOptions: {
      direction: 'TB',
      levelSeparation: 100,
      nodeSeparation: 50
    },
    requiredDataStructure: 'tree',
    supportsCycles: false
  },
  
  force: {
    id: 'force',
    technicalName: 'force',
    displayName: 'Network Diagram',
    shortName: 'Network',
    description: 'Physics-based layout showing natural clusters and communities',
    bestFor: [
      'Social networks',
      'Package dependencies',
      'Knowledge graphs',
      'Relationship mapping',
      'Community detection'
    ],
    icon: '🕸️',
    thumbnail: '/assets/layouts/force.svg',
    decisionPath: {
      hasHierarchy: false,
      hasCentralNode: false,
      showClustering: true
    },
    defaultOptions: {
      strength: -30,
      distance: 100,
      iterations: 300
    },
    requiredDataStructure: 'graph',
    supportsCycles: true
  },
  
  radial: {
    id: 'radial',
    technicalName: 'radial',
    displayName: 'Mind Map',
    shortName: 'Mind Map',
    description: 'Central concept with branches radiating outward',
    bestFor: [
      'Brainstorming',
      'Concept mapping',
      'Topic exploration',
      'Idea organization',
      'Presentations'
    ],
    icon: '🧠',
    thumbnail: '/assets/layouts/radial.svg',
    decisionPath: {
      hasHierarchy: false,
      hasCentralNode: true
    },
    defaultOptions: {
      radius: 100,
      angleStart: 0,
      angleRange: 360
    },
    requiredDataStructure: 'tree',
    supportsCycles: false
  },
  
  dagre: {
    id: 'dagre',
    technicalName: 'dagre',
    displayName: 'Flowchart',
    shortName: 'Flowchart',
    description: 'Sequential process flow with clear direction',
    bestFor: [
      'Process workflows',
      'State machines',
      'Build pipelines',
      'Decision trees',
      'Task sequences'
    ],
    icon: '📊',
    thumbnail: '/assets/layouts/dagre.svg',
    decisionPath: {
      hasHierarchy: true,
      isProcessFlow: true
    },
    defaultOptions: {
      rankdir: 'TB',
      ranksep: 50,
      nodesep: 50
    },
    requiredDataStructure: 'dag',
    supportsCycles: false
  },
  
  sankey: {
    id: 'sankey',
    technicalName: 'sankey',
    displayName: 'Flow Diagram',
    shortName: 'Sankey',
    description: 'Visualize quantities flowing between nodes',
    bestFor: [
      'Budget allocation',
      'Energy flows',
      'Data pipelines',
      'Material flows',
      'Traffic analysis'
    ],
    icon: '💧',
    thumbnail: '/assets/layouts/sankey.svg',
    decisionPath: {
      hasHierarchy: false,
      hasCentralNode: false,
      quantitiesImportant: true
    },
    defaultOptions: {
      nodeWidth: 15,
      nodePadding: 10,
      showValues: true
    },
    requiredDataStructure: 'flow',
    supportsCycles: false
  },
  
  circular: {
    id: 'circular',
    technicalName: 'circular',
    displayName: 'Circular Network',
    shortName: 'Circular',
    description: 'Nodes arranged in a circle showing equal importance',
    bestFor: [
      'Peer networks',
      'Circular dependencies',
      'Network topology',
      'Cycles',
      'Equal relationships'
    ],
    icon: '⭕',
    thumbnail: '/assets/layouts/circular.svg',
    decisionPath: {
      hasHierarchy: false,
      hasCentralNode: false,
      allNodesEqual: true
    },
    defaultOptions: {
      radius: 200,
      ordering: 'topology'
    },
    requiredDataStructure: 'graph',
    supportsCycles: true
  },
  
  grid: {
    id: 'grid',
    technicalName: 'grid',
    displayName: 'Simple Grid',
    shortName: 'Grid',
    description: 'Uniform spacing in rows and columns - lightweight display',
    bestFor: [
      'Image galleries',
      'Product catalogs',
      'Card grids',
      'Simple displays',
      'No interactions needed'
    ],
    icon: '⊞',
    thumbnail: '/assets/layouts/grid.svg',
    decisionPath: {
      relationshipsDontMatter: true,
      noInteractionsNeeded: true
    },
    defaultOptions: {
      columns: 4,
      rowHeight: 100,
      columnWidth: 100,
      showHeaders: false,
      gap: 16
    },
    requiredDataStructure: 'any',
    supportsCycles: true,
    note: 'For complex tables with sorting/filtering, use Spreadsheet component instead'
  }
};

/**
 * Get layout metadata by ID
 */
export function getLayoutMetadata(layoutId) {
  return CHART_LAYOUTS[layoutId] || null;
}

/**
 * Get all layout IDs
 */
export function getAllLayoutIds() {
  return Object.keys(CHART_LAYOUTS);
}

/**
 * Get layouts filtered by criteria
 */
export function getLayoutsByFilter(filter) {
  return Object.values(CHART_LAYOUTS).filter(layout => {
    if (filter.supportsHierarchy && !layout.decisionPath.hasHierarchy) return false;
    if (filter.supportsCycles !== undefined && layout.supportsCycles !== filter.supportsCycles) return false;
    if (filter.dataStructure && layout.requiredDataStructure !== filter.dataStructure && layout.requiredDataStructure !== 'any') return false;
    return true;
  });
}
